// WantlistTable: Virtualisierte Tabelle für die Wantlist-Seite.
// Rendert gruppierte Karten mit Gruppen-Header, Spalten-Header und Karten-Reihen.
// Verwendet useWindowVirtualizer, damit die Seite natürlich scrollt — kein
// eigener Scroll-Container, keine feste Höhe.
import { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Upload } from 'lucide-react';
import { formatCategoryName } from '../ui/formatCategoryName';
import './WantlistTable.css';

/**
 * Label-Anzeige: stufenlos schrumpfende Chips. Pro Chip nimmt der Browser
 * via flex-shrink: 1 + min-width (Dot-Breite) den verfügbaren Platz und
 * blendet den Text beim Schrumpfen automatisch via overflow:hidden aus.
 * Kein State, kein ResizeObserver, kein Mess-Code — der Browser regelt es.
 */
function LabelDisplay({ labels }) {
  if (!labels?.length) return null;
  return (
    <div className="label-chips">
      {labels.map((l, i) => (
        <span
          key={i}
          className="label-chip"
          style={{ '--label-color': l.color }}
          title={l.name}
        >
          <span className="label-chip__dot" />
          <span className="label-chip__text">{l.name}</span>
        </span>
      ))}
    </div>
  );
}

export default function WantlistTable({
  groupedCards,
  selectedCard,
  onSelectCard,
  onToggleCard,
  onExportGroup,
  groupBy,
}) {
  // Flatten group → header + cards in eine einzige Liste.
  const flatRows = useMemo(() => {
    const rows = [];
    for (const [groupName, groupCards] of Object.entries(groupedCards || {})) {
      const groupMissing = groupCards.reduce((s, c) => s + (c.missing || 0), 0);
      const groupValue = groupCards.reduce(
        (s, c) => s + (c.price || 0) * (c.missing || 0),
        0,
      );
      // Wenn ALLE Karten der Gruppe excluded sind, gilt die Gruppe als excluded.
      // Praktisch nur bei groupBy='category' relevant — bei anderen Gruppierungen
      // (Set, Typ etc.) sind selten alle Karten excluded und der Marker wäre irreführend.
      const isExcludedGroup =
        groupCards.length > 0 && groupCards.every((c) => c.isExcluded);
      rows.push({
        type: 'header',
        groupName,
        count: groupMissing,
        value: groupValue,
        cards: groupCards,
        isExcluded: isExcludedGroup,
      });
      for (const card of groupCards) {
        rows.push({ type: 'card', card });
      }
    }
    return rows;
  }, [groupedCards]);

  // Window-basierte Virtualisierung: Wir messen den Offset des Tabellen-Containers
  // vom Document-Top und übergeben ihn als scrollMargin, damit die Virtualisierung
  // weiß, wo der Tabellen-Bereich beginnt.
  const tableRef = useRef(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!tableRef.current) return;
    const update = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setScrollMargin(rect.top + window.scrollY);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [flatRows.length]);

  const virtualizer = useWindowVirtualizer({
    count: flatRows.length,
    estimateSize: (i) => {
      const row = flatRows[i];
      if (!row) return 40;
      if (row.type === 'header') return 44;
      return 40;
    },
    overscan: 20,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  if (flatRows.length === 0) {
    return null;
  }

  return (
    <div className="wantlist-table">
      <div className="wantlist-row wantlist-row--head" aria-hidden={false}>
        <div className="data-table__head-cell wantlist-cell--name-head">Karte</div>
        <div className="data-table__head-cell wantlist-cell--labels">Labels</div>
        <div className="data-table__head-cell data-table__cell--num wantlist-cell--missing">Fehlt</div>
        <div className="data-table__head-cell data-table__cell--num wantlist-cell--owned">Besitz</div>
        <div className="data-table__head-cell data-table__cell--num wantlist-cell--price">Preis</div>
        <div className="data-table__head-cell wantlist-cell--friends">Freunde</div>
      </div>
      <div
        className="wantlist-table__inner"
        ref={tableRef}
        style={{ height: `${totalSize}px`, position: 'relative' }}
      >
          {virtualItems.map((virtualItem) => {
            const row = flatRows[virtualItem.index];
            const style = {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${virtualItem.start - scrollMargin}px)`,
              height: `${virtualItem.size}px`,
            };

            if (row.type === 'header') {
              return (
                <div
                  key={virtualItem.key}
                  className="wantlist-group-header"
                  style={style}
                >
                  <span className="wantlist-group-header__title">
                    {groupBy === 'category' ? formatCategoryName(row.groupName) : row.groupName}
                    {row.isExcluded && (
                      <span className="wantlist-group-header__excluded"> (nicht im Deck)</span>
                    )}
                  </span>
                  <span className="wantlist-group-header__stats">
                    {row.count} fehlen · {row.value.toFixed(2)} €
                  </span>
                  <button
                    type="button"
                    className="btn-icon wantlist-group-header__export"
                    onClick={() => onExportGroup?.(row.groupName, row.cards)}
                    aria-label={`${row.groupName} exportieren`}
                    title={`${row.groupName} exportieren`}
                  >
                    <Upload size={14} aria-hidden />
                  </button>
                </div>
              );
            }

            // type === 'card'
            const card = row.card;
            const isSelected =
              selectedCard?.deckCardId === card.deckCardId;

            return (
              <div
                key={virtualItem.key}
                className={[
                  'wantlist-row',
                  'data-table__row',
                  card.isGrayed ? 'data-table__row--muted' : '',
                  isSelected ? 'data-table__row--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={style}
                onClick={() => onSelectCard?.(card)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectCard?.(card);
                  }
                }}
              >
                <div className="wantlist-cell data-table__cell wantlist-cell--checkbox">
                  <input
                    type="checkbox"
                    checked={!!card.isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleCard?.(card.deckCardId)}
                    aria-label={`Export ${card.name}`}
                  />
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--name data-table__cell--primary">
                  <span className="wantlist-cell__cardname">{card.name}</span>
                  {card.printLocked && card.setCode && (
                    <span className="wantlist-cell__setcode">{card.setCode.toUpperCase()}</span>
                  )}
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--labels">
                  <LabelDisplay labels={card.labels} />
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--missing data-table__cell--primary data-table__cell--num">
                  {card.missing > 0 ? `${card.missing}x` : '—'}
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--owned data-table__cell--num">
                  {card.owned} / {card.quantity}
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--price data-table__cell--num">
                  {card.price ? `${card.price.toFixed(2)} €` : '—'}
                </div>
                <div className="wantlist-cell data-table__cell wantlist-cell--friends">
                  {card.friends?.length > 0
                    ? card.friends.map((f) => f.name).join(', ')
                    : '—'}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
